import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useDispatch, useSelector } from 'react-redux'

import CreateTeam from './CreateTeam'
import Footer from './Footer'
import RejoinTeam from './RejoinTeam'
import TeamExists from './TeamExists'

import styles from './styles.module.scss'

import { TeamMemberActionDialogs } from '@/components/TeamMemberActionDialogs'
import { useToast } from '@/components/Toast/ToastContext'
import { useTeamMemberActions } from '@/hooks/useTeamMemberActions'
import Nav from '@/pages/app/AppLayout/Nav'
import { AppDispatch } from '@/store'
import {
  fetchTeamDomainStatusThunk,
  selectIsDomainLoading,
  selectTeamDomainStatus,
} from '@/store/features/domain/domainSlice'
import {
  createEnterpriseThunk,
  getEnterpriseThunk,
  selectEmail,
  selectSubscription,
} from '@/store/features/user/userSlice'
import { TeamDomainStatus } from '@/types'
import { generateTeamNameFromDomain } from '@/utils'
import { canRejoinEnterprise } from '@/utils/subscription'

function isDomainBlockedError(err: unknown): err is { code: 'DOMAIN_BLOCKED' } {
  return typeof err === 'object' && err !== null && 'code' in err && err.code === 'DOMAIN_BLOCKED'
}

type Status = 'ready' | 'loading' | 'error'

export default function GetStartedPage() {
  const [orgName, setOrgName] = useState('')
  const [status, setStatus] = useState<Status>('ready')
  const [salesAssistanceError, setSalesAssistanceError] = useState(false)
  const dispatch = useDispatch<AppDispatch>()
  const teamDomainStatus = useSelector(selectTeamDomainStatus)
  const email = useSelector(selectEmail)
  const isDomainLoading = useSelector(selectIsDomainLoading)
  const subscription = useSelector(selectSubscription)
  const canRejoin = canRejoinEnterprise(subscription)

  const { updateToast } = useToast()
  const { inviteMembers, activeDialog, closeDialog, executeAction } = useTeamMemberActions()

  const orgDomain =
    isDomainLoading || teamDomainStatus === null || teamDomainStatus === TeamDomainStatus.Forbidden
      ? null
      : email.split('@')[1]

  // When we get a domain, use it to generate an initial name
  useEffect(() => {
    if (orgDomain) {
      setOrgName(generateTeamNameFromDomain(orgDomain))
    }
  }, [orgDomain])

  useEffect(() => {
    dispatch(fetchTeamDomainStatusThunk())
  }, [dispatch])

  const handleSubmit = async (emails: string[], autoInviteUsers: boolean) => {
    if (!orgName.trim()) {
      updateToast('Please enter an organization name', 'error')
      return
    }

    setStatus('loading')
    try {
      // Create the enterprise
      await dispatch(
        createEnterpriseThunk({
          name: orgName,
          autoInviteUsers,
        })
      ).unwrap()

      // Refetch enterprise with subscription (includes retry logic if subscription isn't ready yet)
      await dispatch(getEnterpriseThunk({ needSubscription: true })).unwrap()

      // Invite initial team members using bulk invite
      if (emails.length > 0) {
        const result = await inviteMembers(emails.map((email) => ({ email, role: 'admin' })))
        // Single-invite and error outcomes are toasted by the hook; bulk
        // results are returned for the caller to present.
        if (result && result !== 'error') {
          const successCount = result.successful.length
          const failedCount = result.failed.length
          if (failedCount === 0) {
            updateToast(`Successfully invited ${successCount} user${successCount > 1 ? 's' : ''}`, 'success')
          } else if (successCount > 0) {
            updateToast(
              `Invited ${successCount} user${successCount > 1 ? 's' : ''}, but ${failedCount} failed`,
              'success'
            )
          } else {
            updateToast(`Failed to invite ${failedCount} user${failedCount > 1 ? 's' : ''}`, 'error')
          }
        }
      }

      // Set status back to ready after successful creation
      setStatus('ready')
    } catch (err) {
      console.error('Failed to create organization', err)
      /* eslint-disable @typescript-eslint/no-explicit-any */
      // Prefer backend-provided error detail or rejectWithValue string from .unwrap()
      const detail =
        (typeof err === 'string' && err) ||
        (err as any)?.detail ||
        (err as any)?.response?.data?.detail ||
        (err as any)?.message
      /* eslint-enable @typescript-eslint/no-explicit-any */

      // Check for blocked domain error (requires sales assistance)
      if (isDomainBlockedError(err)) {
        setStatus('ready')
        setSalesAssistanceError(true)
      } else if (detail === '400: Forbidden domain') {
        setStatus('ready')
        updateToast('Cannot create organization with a public domain', 'error')
      } else if (
        typeof detail === 'string' &&
        detail.toLowerCase().includes('already belongs to an enterprise')
      ) {
        try {
          const enterprise = await dispatch(getEnterpriseThunk({ needSubscription: false })).unwrap()
          if (enterprise?.id) {
            updateToast("You're already on a team. Redirecting...", 'success')
          } else {
            setStatus('ready')
            updateToast('Failed to load your team. Please try again.', 'error')
          }
        } catch {
          setStatus('ready')
          updateToast('Failed to load your team. Please try again.', 'error')
        }
      } else if (typeof detail === 'string' && detail) {
        setStatus('ready')
        updateToast(detail, 'error')
      } else {
        setStatus('ready')
        updateToast('Failed to create organization', 'error')
      }
    }
  }

  return (
    <>
      <TeamMemberActionDialogs activeDialog={activeDialog} onClose={closeDialog} onExecute={executeAction} />
      <Helmet>
        <title>Wispr Flow | Get Started</title>
      </Helmet>
      <div className={styles.root}>
        <Nav preSetup />
        <div className={styles.container}>
          <div className={styles.contentContainer}>
            {/* If user can rejoin a previous team, show rejoin option */}
            {canRejoin ? (
              <RejoinTeam />
            ) : teamDomainStatus === TeamDomainStatus.Exists ? (
              // If a team for this users domain exists, let them join/request to join
              <TeamExists />
            ) : (
              // Otherwise, prompt the user to create a team
              <CreateTeam
                loading={status === 'loading' || isDomainLoading}
                orgName={orgName}
                setOrgName={setOrgName}
                orgDomain={orgDomain ?? undefined}
                onSubmit={handleSubmit}
                salesAssistanceError={salesAssistanceError}
              />
            )}
          </div>
          {teamDomainStatus !== TeamDomainStatus.Exists && <Footer />}
        </div>
      </div>
    </>
  )
}
